import { useContext, useMemo } from 'react';
import { ScenarioStatesProviderContext } from '../providers/StatesProvider';
import { getOrbitalInsightDetails, getCurrentOrbitExposure } from '../../../CommonComponents/orbital-insights/helperFunction';
import { ExposureTiming } from '../../../CommonComponents/orbital-insights/orbital-insights-type';

const EMPTY_SUN_EXPOSURES: ExposureTiming[] = [];

const useThermalHeatmap = (currentTimeEpoch: number | null = null) => {
    const context = useContext(ScenarioStatesProviderContext);
    if (context === undefined) {
        throw new Error('useThermalHeatmap must be used within a StatesProvider');
    }
    // Extract thermal data for the currently selected group
    const selectedGroupId = context.selectedGroupSatellite?.groupId;
    const groupThermalData = selectedGroupId && context.thermalHeatmapData
        ? context.thermalHeatmapData[selectedGroupId]
        : null;

    const selectedSatelliteId = context.selectedGroupSatellite?.satelliteId;
    const orbitalInsightsData = context.formData.assets.orbitalInsights.data;
    const selectedSatelliteInsights = selectedSatelliteId && orbitalInsightsData
        ? orbitalInsightsData[selectedSatelliteId]
        : null;

    // Orbit list depends only on the insights/start option, not on the live time
    const orbits = useMemo(
        () => (selectedSatelliteInsights ? getOrbitalInsightDetails(selectedSatelliteInsights, context.orbitStartOption) : null),
        [selectedSatelliteInsights, context.orbitStartOption]
    );

    const { currentOrbit, currentExposure } = useMemo(() => {
        if (!orbits || currentTimeEpoch === null) {
            return { currentOrbit: null, currentExposure: null };
        }
        const { orbit, exposure } = getCurrentOrbitExposure(orbits, currentTimeEpoch);
        return { currentOrbit: orbit, currentExposure: exposure };
    }, [orbits, currentTimeEpoch]);

    // Presim orbit/attitude for the selected satellite (quaternionData)
    const presimOrbitDetails = selectedSatelliteId
        ? context.formData?.assets?.presim?.data?.[selectedSatelliteId]?.orbitDetails ?? null
        : null;

    return {
        presimOrbitDetails,
        isThermalOpen: context.isThermalOpen,
        setIsThermalOpen: context.setIsThermalOpen,
        isThermalFullscreen: context.isThermalFullscreen,
        setIsThermalFullscreen: context.setIsThermalFullscreen,
        thermalHeatmapData: groupThermalData,
        thermalEnvParams: context.thermalEnvParams,
        thermalModel: context.thermalModel,
        orbitalInsights: orbitalInsightsData,
        scenarioResponse: context.scenarioResponse,
        selectedGroupSatellite: context.selectedGroupSatellite,
        currentOrbit,
        currentExposure,
        sunExposures: selectedSatelliteInsights?.allOrbits.sunExposures ?? EMPTY_SUN_EXPOSURES,
    };
};

export default useThermalHeatmap;
